import { toCutsceneJournalEntryId } from '@sorrem/shared';
import { TabStrip, type TabDef } from '@sorrem-client/ui/components';
import { useAppNavigate, useCharacterCommissions, useCompendium, useJournal } from '@sorrem-client/ui/hooks';
import { CutscenePlayer } from '@sorrem-client/ui/features/Cutscene';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { findCompendiumVision, resolveCompendiumSelection, type JourneySection, type CompendiumSelection } from './compendium-pages';
import { CompendiumBook } from './components/CompendiumBook';
import { JournalBook } from './components/JournalBook';
import { VisionPlayer } from './components/VisionPlayer';
import styles from './JourneyMainPanel.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

const journeySections: TabDef<JourneySection>[] = [
    { id: 'journal', label: 'Journal' },
    { id: 'compendium', label: 'Compendium' },
];

/** The Journey before the Compendium opens: the Journal alone. */
const journalOnlySections = journeySections.filter((tab) => tab.id === 'journal');

interface JourneyMainPanelProps {
    section: JourneySection;
    onSectionChange: (section: JourneySection) => void;
    selectedEntryId: string | null;
    onSelectEntry: (entryId: string) => void;
    compendiumSelection: CompendiumSelection | null;
    onSelectCompendium: (selection: CompendiumSelection) => void;
}

/**
 * The Journey: the character's own story (the Journal) and the visions
 * they have received (the Compendium), both kept in one field journal, the
 * Compendium written in the back of it. It lies open on the writing table: no panel frame, and the whole
 * screen below the header is theirs. What can be done with an entry or a
 * vision is on its own page. The Compendium stays closed until the clerk
 * names it, at the end of the Mill Road commission (`isCompendiumLocked`);
 * until then it is hidden while commissions load, so it never flashes open.
 */
export function JourneyMainPanel({
    section,
    onSectionChange,
    selectedEntryId,
    onSelectEntry,
    compendiumSelection,
    onSelectCompendium,
}: JourneyMainPanelProps): JSX.Element {
    const { entries, isLoaded } = useJournal();
    const { compendium, isLoaded: isCompendiumLoaded } = useCompendium();
    const { isCompendiumLocked, isLoading: isCommissionsLoading } = useCharacterCommissions();
    const isCompendiumOpen = !isCommissionsLoading && !isCompendiumLocked;
    const navigate = useAppNavigate();
    // A replay is its own route, /character/journey/cutscenes/:cutsceneId; so is a relive, /character/journey/visions/:visionId.
    const { cutsceneId: replayingId, visionId: relivingId } = useParams<{ cutsceneId?: string; visionId?: string }>();
    const isReplayWatched = entries.some((entry) => entry.kind === 'cutscene' && entry.cutscene.cutsceneId === replayingId);
    const relivingVision = relivingId !== undefined ? findCompendiumVision(compendium, relivingId) : undefined;

    useEffect(() => {
        if (replayingId === undefined) {
            return;
        }
        // Keep the replayed cutscene selected, so the book is where it was afterward.
        onSectionChange('journal');
        onSelectEntry(toCutsceneJournalEntryId(replayingId));
        // Only cutscenes the character has watched can be replayed; a link to
        // any other would show story they haven't reached.
        if (isLoaded && !isReplayWatched) {
            navigate('/character/journey', { replace: true });
        }
    }, [isLoaded, isReplayWatched, navigate, onSectionChange, onSelectEntry, replayingId]);

    useEffect(() => {
        if (relivingId === undefined) {
            return;
        }
        onSectionChange('compendium');
        onSelectCompendium({ kind: 'vision', visionId: relivingId });
        // Only a vision the character has received can be relived, and only once the Compendium is open.
        if ((isCompendiumLoaded && relivingVision === undefined) || (!isCommissionsLoading && isCompendiumLocked)) {
            navigate('/character/journey', { replace: true });
        }
    }, [isCommissionsLoading, isCompendiumLoaded, isCompendiumLocked, navigate, onSectionChange, onSelectCompendium, relivingId, relivingVision]);

    // A section left on the Compendium (a relive link, kept state) shows the Journal while it is closed.
    const visibleSection: JourneySection = isCompendiumOpen ? section : 'journal';

    const openVision = (visionId: string): void => {
        onSectionChange('compendium');
        onSelectCompendium({ kind: 'vision', visionId });
    };

    return (
        <div className={styles.journeyPanel}>
            <TabStrip
                className={styles.sectionSwitch}
                tabs={isCompendiumOpen ? journeySections : journalOnlySections}
                activeTab={visibleSection}
                onTabChange={onSectionChange}
                {...toTourTargetProps('journey-books')}
            />

            {visibleSection === 'journal' ? (
                <JournalBook
                    entries={entries}
                    selectedEntryId={selectedEntryId}
                    onSelectEntry={onSelectEntry}
                    onOpenVision={isCompendiumOpen ? openVision : null}
                />
            ) : (
                <CompendiumBook
                    compendium={compendium}
                    selection={resolveCompendiumSelection(compendium, compendiumSelection)}
                    onSelect={onSelectCompendium}
                />
            )}

            {/* Replaying only watches it again; it's already recorded as seen. */}
            {replayingId !== undefined && isReplayWatched && (
                <CutscenePlayer cutsceneId={replayingId} onFinished={() => navigate('/character/journey')} />
            )}
            {isCompendiumOpen && relivingVision !== undefined && (
                <VisionPlayer vision={relivingVision} onFinished={() => navigate('/character/journey')} />
            )}
        </div>
    );
}
